import { faMessage, faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { useContext, useEffect, useState } from 'react'
import z from 'zod'
import { useNewMessageMutation } from '~/api/mutations/action'
import { useApplicantsHead } from '~/api/queries/applicant'
import { useBrandInfos, useMessageSendingInfo } from '~/api/queries/global'
import { useMeInfos } from '~/api/queries/user'
import { Dropdown, Form, InputCol, Submit, TextInput } from '~/components/Form'
import { TextareaInput } from '~/components/Form/Input/TextareaInput'
import { SubmitAlignment } from '~/components/Form/Submit'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { alertLink } from '~/components/Global/Interactions/Alert/Alert.css'
import { ButtonVariation } from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/Toast'
import {
  smsInfoBar,
  smsInfoBarItem
} from '~/components/Scopes/Actions/Actions.css'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { classicLink } from '~/styles/app.css'
import { toast, utf8ToGsm0338 } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { useCloseAction } from './hooks/useCloseAction'

const formSchema = z.object({
  agencyId: z.coerce.number(),
  message: z.string().min(1, { message: 'Le message ne peut pas être vide' })
})

type FormValues = z.infer<typeof formSchema>

type MessageActionProps = {
  applicantIds: number[]
  removeApplicantIdFromList?: (applicantId: number) => void
  onClose?: () => void
}

export const MessageAction = ({
  applicantIds,
  removeApplicantIdFromList,
  onClose
}: MessageActionProps) => {
  const { data: applicantDataHead } = useApplicantsHead({
    applicantIds: applicantIds ?? []
  })
  const { data: brandInfos } = useBrandInfos()
  const [agencyId, setAgencyId] = useState<number | null>(null)
  const [applicantMissPhone, setApplicantMissPhone] = useState<number[]>([])
  const [message, setMessage] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [currentApplicantIds, setCurrentApplicantIds] = useState<number[]>(
    applicantIds ?? []
  )
  const { data: sendingInfo } = useMessageSendingInfo({
    agencyId
  })
  const userInfo = useMeInfos()
  const newMessageMutation = useNewMessageMutation()
  const { setIsDirty } = useContext(AsideActionContext)
  const closeAction = useCloseAction()

  // Handle applicantIds change from parent if new action is triggered
  useEffect(() => {
    if (applicantIds) {
      setCurrentApplicantIds(applicantIds)
    }
  }, [applicantIds])

  const onActionClose = () => {
    closeAction().then((confirm) => {
      if (confirm) {
        onClose?.()
      }
    })
  }

  const onSubmit = async (values: FormValues) => {
    setError(null)
    setApplicantMissPhone([])
    await newMessageMutation.mutateAsync(
      {
        applicants: currentApplicantIds,
        agencyId: values.agencyId,
        message: values.message
      },
      {
        onSuccess: () => {
          closeAction(true)
          onClose?.()
          toast(
            'Message envoyé',
            'Le message a bien été envoyé',
            ToastType.Success
          )
        },
        onError: (error) => {
          const potentialApplicantMissPhone = error.response?.message?.match(
            /Applicant (\d+) does not have a mobile phone number/i
          )?.[1]
          if (potentialApplicantMissPhone) {
            setApplicantMissPhone([
              Number.parseInt(potentialApplicantMissPhone)
            ])
          }
          const errorMessage = "Erreur lors de l'envoi du message"
          setError(
            error.httpStatus === 402 ? 'Pas assez de crédit SMS' : errorMessage
          )
          toast(
            'Une erreur est survenue',
            error.httpStatus === 402
              ? "Vous n'avez pas assez de crédit SMS pour envoyer ce message"
              : errorMessage,
            ToastType.Danger
          )
        }
      }
    )
  }

  useEffect(() => {
    if (!agencyId && userInfo && userInfo.agencies[0] !== undefined) {
      setAgencyId(userInfo.agencies[0].id)
    }
  }, [userInfo, agencyId])

  const warningMessage =
    "L'utilisation de certains caractères tels que â, ê, ô ou € multipliant par 2 le nombre de SMS envoyés, la saisie de ces caractères est bloquée."

  let content = <Loader small={true} forceText={true} />

  if (sendingInfo && userInfo) {
    const nbAgencies = userInfo.agencies.length
    // (single - stop) OR (multi*count - stop)
    let maxLength = sendingInfo.singleSMSLength - sendingInfo.stopSMSLength
    let smsCount = 1
    if (sendingInfo.smsMax > 1 && message.length > maxLength) {
      smsCount = Math.ceil(
        (message.length + sendingInfo.stopSMSLength) /
          sendingInfo.multiSMSLength
      )
      maxLength =
        smsCount * sendingInfo.multiSMSLength - sendingInfo.stopSMSLength
    }
    const hardMaxLength =
      sendingInfo.smsMax * sendingInfo.multiSMSLength -
      sendingInfo.stopSMSLength

    content = (
      <Form
        onSubmit={onSubmit}
        schema={formSchema}
        globalError={error}
        globalErrorInSubmit={true}
      >
        <InputCol>
          {nbAgencies > 1 && (
            <Dropdown
              name="agencyId"
              options={
                userInfo?.agencies?.map((option) => ({
                  label: option.name,
                  value: String(option.id)
                })) ?? []
              }
              required
              defaultValue={agencyId ? String(agencyId) : undefined}
              onChange={(value) => {
                setAgencyId(Number.parseInt(value))
                setIsDirty(true)
              }}
              label="Agence expéditrice"
            />
          )}
          {nbAgencies === 1 && (
            <TextInput
              type="hidden"
              name="agencyId"
              value={userInfo?.agencies[0]?.id}
            />
          )}
          <TextareaInput
            name="message"
            placeholder="Votre message"
            rows={5}
            required
            maxLength={hardMaxLength}
            value={message}
            onChange={(e) => {
              const value = (e.target as HTMLTextAreaElement).value
              const sanitized = utf8ToGsm0338(value)
              if (sanitized !== value) {
                toast('Caractères interdits', warningMessage, ToastType.Warning)
              }
              setMessage(sanitized)
              setIsDirty(true)
            }}
            contextInfo={warningMessage}
          />
          <div className={smsInfoBar}>
            <div>
              <span className={smsInfoBarItem}>
                <strong>
                  {message.length} / {maxLength}
                </strong>{' '}
                caractères + (STOP SMS).
              </span>{' '}
              <span className={smsInfoBarItem}>
                SMS décomptés :{' '}
                <strong>
                  {smsCount}/{sendingInfo.smsMax}
                </strong>
              </span>
            </div>
            <div className={smsInfoBarItem}>
              Solde SMS : <strong>{sendingInfo.smsRemaining}</strong>
            </div>
          </div>
          <Submit
            buttonVariation={ButtonVariation.Primary}
            submitAlignment={SubmitAlignment.Right}
            loadingText="Envoi..."
            successText="Envoyé !"
            onSuccessReset={false}
          >
            <FontAwesomeIcon icon={faPaperPlaneTop} />
            Envoyer
          </Submit>
        </InputCol>
        <div>
          {applicantMissPhone.map((applicantId) => {
            const applicantData = applicantDataHead?.find(
              (applicant) => applicant.id === applicantId
            )
            return (
              <Alert
                key={applicantId}
                variation={AlertVariation.Danger}
                message={`Le candidat ${applicantData?.firstname} ${applicantData?.lastname} n'a pas de numéro de téléphone valide.`}
                onDismiss={onActionClose}
              >
                <div>
                  <span
                    className={clsx(classicLink, alertLink)}
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(
                        `${brandInfos?.legacyUrl}/admin/candidats/edit/${applicantId}`
                      )
                    }}
                    onAuxClick={(e) => {
                      e.preventDefault()
                      window.open(
                        `${brandInfos?.legacyUrl}/admin/candidats/edit/${applicantId}`
                      )
                    }}
                  >
                    Modifier le candidat
                  </span>
                  {removeApplicantIdFromList &&
                    (currentApplicantIds?.length ?? 0) > 1 && (
                      <span
                        className={clsx(classicLink, alertLink)}
                        onClick={(e) => {
                          e.preventDefault()
                          removeApplicantIdFromList(applicantId)
                          setCurrentApplicantIds((prev) =>
                            prev.filter((id) => id !== applicantId)
                          )
                          setApplicantMissPhone((prev) =>
                            prev.filter((id) => id !== applicantId)
                          )
                        }}
                      >
                        Retirer de l'envoi
                      </span>
                    )}
                  {currentApplicantIds?.length === 1 && onClose && (
                    <span
                      className={clsx(classicLink, alertLink)}
                      onClick={onActionClose}
                    >
                      Annuler l'envoi
                    </span>
                  )}
                </div>
              </Alert>
            )
          })}
        </div>
      </Form>
    )
  }

  if (userInfo && userInfo.agencies.length === 0) {
    content = (
      <Alert
        message="Vous n'êtes rattaché à aucune agence. Vous ne pouvez pas envoyer de
        message."
        variation={AlertVariation.Warning}
        onDismiss={onActionClose}
      />
    )
  }

  if (sendingInfo && sendingInfo.service === null) {
    content = (
      <Alert
        message="Le service d'envoi de SMS n'est pas configuré pour votre agence. Vous ne
        pouvez pas envoyer de message."
        variation={AlertVariation.Warning}
        onDismiss={onActionClose}
      />
    )
  }

  return (
    <AsideAction
      title="Envoyer un SMS"
      icon={faMessage}
      applicantIds={currentApplicantIds}
      onClose={onClose}
    >
      {content}
    </AsideAction>
  )
}
